import "gantt-task-react/dist/index.css";
import { Gantt, ViewMode } from "gantt-task-react";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast, Bounce } from "react-toastify";
import "./styles.css";

import api from "../../services/api.js";
import ModalEditNameEpic from "../ModalEditNameEpic/ModalEditNameEpic.js";
import ModalCreateUserStory from "../ModalCreateUserStory/ModalCreateUserStory.js";
import ModalEditUserStoryAttributes from "../ModalEditUserStoryAttributes/ModalEditUserStoryAttributes.js";
import ModalManageDependencies from "./ModalManageDependencies.js";
import {
  cnsumColor,
  buildGanttTasks,
  buildStoryTask,
  buildStoryExecutionTask,
  fetchStoriesWithAttributes,
  normalizeString,
  addDays,
  makePlaceholderRow,
  HOURS_PER_DAY,
  TOTAL_LIST_WIDTH,
  parseLocalDate,
} from "./helpers";
import { TaskListHeader } from "./TaskListHeader";
import { TaskListTable } from "./TaskListTable";

function GanttChartEnhanced({
  idProjeto,
  epicoSelecionado,
  statusSelecionado,
  termoPesquisa,
  dataInicio,
  setGanttHasData,
}) {
  const [view, setView] = useState(ViewMode.Week);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [taskSelected, setTaskSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [epicStories, setEpicStories] = useState({});
  const [expandedEpics, setExpandedEpics] = useState({});
  const [storiesLoading, setStoriesLoading] = useState({});
  const [openCreateUserStoryModal, setOpenCreateUserStoryModal] = useState(false);
  const [selectedEpicForStory, setSelectedEpicForStory] = useState(null);
  const [openEditUserStoryAttributesModal, setOpenEditUserStoryAttributesModal] = useState(false);
  const [selectedStoryForAttributes, setSelectedStoryForAttributes] = useState(null);
  const [selectedEpicForEditedStory, setSelectedEpicForEditedStory] = useState(null);
  const [openManageDependenciesModal, setOpenManageDependenciesModal] = useState(false);
  const [selectedEntityForDependencies, setSelectedEntityForDependencies] = useState(null);
  const [dependenciesData, setDependenciesData] = useState([]);
  const ganttRef = useRef(null);
  const plannedStartAttrIdRef = useRef(null);

  // ── Busca e filtragem ──────────────────────────────────────────────────────

  const filterRaw = useCallback(
    (rawTasks) =>
      rawTasks.filter((task) => {
        if (termoPesquisa) {
          const term = normalizeString(termoPesquisa);
          return normalizeString(task.name || task.title || "").includes(
            term
          );
        }
        const matchEpico =
          !epicoSelecionado ||
          epicoSelecionado === "0" ||
          task.title === epicoSelecionado;
        const matchStatus =
          !statusSelecionado ||
          statusSelecionado === "0" ||
          task.status === statusSelecionado;
        return matchEpico && matchStatus;
      }),
    [termoPesquisa, epicoSelecionado, statusSelecionado]
  );

  const dependencyCandidates= useMemo(() => {
    const epicCandidates = tasks
      .filter((t) => t.rowKind === "main")
      .map((t) => ({ id: `epic-${t.id}`, name: t.name }));

    const storyCandidates = Object.values(epicStories)
      .flat()
      .map((story) => ({ id: `story-${story.id}`, name: story.subject }));

    return [...epicCandidates, ...storyCandidates];
  }, [tasks, epicStories]);

  const storyIdToEpicId = useMemo(() => {
    const map = {};
    Object.entries(epicStories).forEach(([epicId, stories]) => {
      (stories || []).forEach((story) => {
        map[String(story.id)] = epicId;
      });
    });

    return map;
  }, [epicStories]);

  const getAssigneeName = useCallback(
    (story) => {
      if (!story) return null;

      const assignedId = story.assigned_to;
      if (assignedId !== null && assignedId !== undefined) {
        const assignedUser = users.find(
          (user) => Number(user.id_colaborador) === Number(assignedId)
        );
        if (assignedUser) {
          return assignedUser.nome_colaborador;
        }
      }

      return story.assigned_to_extra_info?.full_name_display || null;
    },
    [users]
  );

  const visibleTasks = useMemo(() => {
    const result = [];
    let pendingEpic = null; // guarda o épico "aberto" até o bloco terminar

    const flushPendingStories = () => {
      if (!pendingEpic) return;
      const { id, task } = pendingEpic;

      if(expandedEpics[id]) {
        const stories = epicStories[id];

        if(storiesLoading[id]) {
          result.push(makePlaceholderRow(id, task, "loadingStories"));
        } else if (stories && stories.length > 0) {
          stories.forEach((story) => {
            const storyTask = buildStoryTask(
              story,
              task,
              dependenciesData,
              storyIdToEpicId,
              getAssigneeName,
            );

            const executionTask = buildStoryExecutionTask(
              story,
              task,
            );

            if (storyTask) {
              result.push(storyTask);
            }

            if (executionTask) {
              result.push(executionTask);
            }
          });
        } else if (stories && stories.length === 0) {
          result.push(makePlaceholderRow(id, task, "emptyStories"));
        }
      }
      pendingEpic = null;
    };

    tasks.forEach((t) => {
      if (t.rowKind === "main") {
        flushPendingStories(); // fecha o bloco do épico antes de abrir o próximo
        pendingEpic = { id: t.id, task: t };
      }
      result.push(t);
    });
    flushPendingStories(); // fecha o último épico do array

    return result;
  }, [
    tasks,
    expandedEpics,
    epicStories,
    dependenciesData,
    storyIdToEpicId,
    storiesLoading,
    getAssigneeName,
  ]);

  // Converte a semântica do projeto (predecessora lista a sucessora)
  // para a semântica da lib (sucessora lista a predecessora),
  // assim a seta sai da direita da predecessora e chega na esquerda da sucessora.
  const ganttTasks = useMemo(() => {
    const isPlanning = (t) => t.rowKind === "main" || t.rowKind === "storyMain";

    const planningIds = new Set(
      visibleTasks.filter(isPlanning).map((t) => String(t.id))
    );

    const predecessorsBySuccessor = {};
    visibleTasks.filter(isPlanning).forEach((pred) => {
      (pred.dependencies || []).map(String).forEach((successorId) => {
        if (!planningIds.has(successorId)) return;
        if (!predecessorsBySuccessor[successorId]) {
          predecessorsBySuccessor[successorId] = [];
        }
        predecessorsBySuccessor[successorId].push(pred.id);
      });
    });

    return visibleTasks.map((t) => ({
      ...t,
      dependencies: isPlanning(t)
        ? predecessorsBySuccessor[String(t.id)] || []
        : [], // barras de consumo/execução não desenham setas
    }));
  }, [visibleTasks]);

  const ganttHeight = useMemo(() => {
    const headerHeight = 50;
    const rowHeight = 44;

    return headerHeight + visibleTasks.length * rowHeight + 20;
  }, [visibleTasks]);

  const getTasks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/api/ganttData", {
        params: {
          projectId: idProjeto || undefined,
          date: dataInicio ? dataInicio.toISOString().split("T")[0] : undefined,
        },
      });
      
      setDependenciesData(data.dependencies);

      const filtered = filterRaw(data.epics);
      const storiesMap = Object.fromEntries(
        filtered.map((epic) => [String(epic.id_epico), epic.stories || []])
      );

      if (filtered.length > 0) {
        setTasks(buildGanttTasks(filtered, data.dependencies, storiesMap));
        setGanttHasData(true);
      } else {
        setTasks([]);
        setGanttHasData(false);
      }
    } catch (error) {
      console.error("Erro ao buscar épicos:", error);
    } finally {
      setLoading(false);
    }
  }, [idProjeto, filterRaw, setGanttHasData, dataInicio]);

  const refetchTasksSilently = useCallback(async () => {
    try {
      const { data } = await api.get("/api/ganttData", {
        params: {
          projectId: idProjeto || undefined,
          date: dataInicio ? dataInicio.toISOString().split("T")[0] : undefined,
        },
      });

      setDependenciesData(data.dependencies);

      const filtered = filterRaw(data.epics);
      const storiesMap = Object.fromEntries(
        filtered.map((epic) => [String(epic.id_epico), epic.stories || []])
      );

      if (filtered.length > 0) {
        const newTasks = buildGanttTasks(filtered, data.dependencies, storiesMap);
        setTasks((prevTasks) => {
          const orderIndex = new Map(prevTasks.map((t, idx) => [t.id, idx]));
          return [...newTasks].sort((a, b) => {
            const idxA = orderIndex.has(a.id) ? orderIndex.get(a.id) : Number.MAX_SAFE_INTEGER;
            const idxB = orderIndex.has(b.id) ? orderIndex.get(b.id) : Number.MAX_SAFE_INTEGER;
            return idxA - idxB;
          });
        });
        setGanttHasData(true);
      } else {
        setTasks([]);
        setGanttHasData(false);
      }
    } catch (error) {
      console.error("Erro ao buscar épicos:", error);
    }
  }, [idProjeto, filterRaw, setGanttHasData, dataInicio]);

  const getUsers = useCallback(async () => {
    try {
      const response = await api.get("/api/buscarUsuarios");
      const all = response.data;

      const filtered = all.map(user => ({
        id_colaborador: user.id,
        nome_colaborador: user.name,
      }));

      if (filtered.length > 0) {
        setUsers(filtered);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    }
  }, []);

  useEffect(() => {
    getTasks();
    getUsers();
  }, [getTasks, getUsers]);

  useEffect(() => {
    const expandedIds = Object.keys(expandedEpics).filter((id) => expandedEpics[id]);
    expandedIds.forEach((epicId) => loadEpicStories(epicId, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInicio]);

  // Definindo apenas a primeira letra do dia
  useEffect(() => {
    if (view !== ViewMode.Day) return;

    const container = document.querySelector(".gantt-container-wrapper");
    if (!container) return;

    const shortenDayLabels = () => {
      container.querySelectorAll("g.calendar text").forEach((el) => {
        const node = el.firstChild;
        if(!node || node.nodeType !== Node.TEXT_NODE) return;

        // Casa "seg., 12" ou "Mon, 5", mas não "janeiro, 2026" (4 dígitos)
        const match = node.nodeValue.trim().match(/^(\p{L})[\p{L}.]*,\s*(\d{1,2})$/u);
        if (!match) return;

        // Altera o nó de texto existente (não recria), para não conflitar com o React
        node.nodeValue = `${match[1].toUpperCase()} ${match[2]}`;
      });
    };

    const observer = new MutationObserver(shortenDayLabels);
    observer.observe(container, { childList: true, subtree: true });
    shortenDayLabels();

    return () => observer.disconnect();
  }, [view, visibleTasks]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  /**
   * Calcula a diferença de dias entre duas datas
   */
  const getDaysDifference = (oldDate, newDate) => {
    return Math.round((newDate - oldDate) / (1000 * 60 * 60 * 24));
  };

  /**
   * Atualiza tarefas dependentes (children) recursivamente
   * Move todas as tarefas que dependem da tarefa arrastada
   */
  const updateDependentTasks = async (
    changedTask,
    currentTasks,
    daysDiff
  ) => {
    const updatedTasks = [...currentTasks];
    const tasksToUpdate = [];

    const collectParents = (taskId, visited = new Set()) => {
      const idStr = String(taskId);
      if (visited.has(idStr)) return [];
      visited.add(idStr);

      const current = updatedTasks.find(
        (t) => t.rowKind === "main" && String(t.id) === idStr
      );

      const parentIds = (current?.dependencies || []).map(String);

      const parents = updatedTasks.filter(
        (t) => t.rowKind === "main" && parentIds.includes(String(t.id))
      );

      let all = [...parents];
      parents.forEach((p) => {
        all = all.concat(collectParents(p.id, visited));
      });
      return all;
    };

    // Remove duplicatas
    const allParents = [
      ...new Map(collectParents(changedTask.id).map((t) => [t.id, t])).values()
    ];

    for (const parent of allParents) {
      const newStart = new Date(parent.start);
      const newEnd = new Date(parent.end);

      newStart.setDate(newStart.getDate() + daysDiff);
      newEnd.setDate(newEnd.getDate() + daysDiff);

      const idx = updatedTasks.findIndex((t) => t.id === parent.id);
      if (idx >= 0) {
        updatedTasks[idx] = { ...updatedTasks[idx], start: newStart, end: newEnd };
      }

      tasksToUpdate.push({ id: parent.id, start: newStart, end: newEnd });
    }

    return { updatedTasks, tasksToUpdate };
  };

  const collectDependentStories = useCallback(
    (storyId, visited = new Set()) => {
      const childKey = `story-${storyId}`;

      if (visited.has(childKey)) return [];
      visited.add(childKey);

      // Pais: linhas em que esta história aparece como filha
      const parents = dependenciesData
        .filter((dep) => (dep.id_filhos || []).map(String).includes(childKey))
        .map((dep) => String(dep.id_pai))
        .filter((id) => id.startsWith("story-"))
        .map((id) => Number(id.replace("story-", "")));

      const result = [...parents];
      parents.forEach((parentId) => {
        result.push(...collectDependentStories(parentId, visited));
      });

      return [...new Set(result)];
    },
    [dependenciesData]
  );

  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  // Regra: sucessora não pode começar antes do fim da predecessora.
  const findViolatedPredecessor = (task) => {
    const oldTask = visibleTasks.find((t) => t.id === task.id);
    if (!oldTask) return null;

    // Só valida quando a barra foi movida para trás (ou redimensionada pelo início)
    if (startOfDay(task.start) >= startOfDay(oldTask.start)) return null;

    const newStart = startOfDay(task.start);

    // Predecessoras: linhas de planejamento cujo `dependencies` contém o id da tarefa arrastada
    return (
      visibleTasks.find(
        (t) =>
          (t.rowKind === "main" || t.rowKind === "storyMain") &&
          (t.dependencies || []).map(String).includes(String(task.id)) &&
          startOfDay(t.end) > newStart
      ) || null
    );
  };

  const handleTaskChange = async (task, children = []) => {
    const violated = findViolatedPredecessor(task);
    if (violated) {
      toast.error(
        `"${task.subject || task.name}" não pode começar antes do fim da predecessora "${violated.subject || violated.name}".`,
        { transition: Bounce },
      );
      return false; // reverte a barra
    }
    try {
      // Identifica se é uma história de usuário pelo formato do ID (story-epicId-storyId)
      const isStory = String(task.id).startsWith("story-");

      if (isStory) {
        // Fluxo para histórias de usuário
        const oldTask = visibleTasks.find(
          (t) => t.id === task.id
        );

        if (oldTask) {
          console.log("Movendo história de usuário:", task.id);
          await updateStoryPlannedStart(task, oldTask);
        }
        // Retorna após atualizar histórias (não continua no fluxo de épicos)
        await refetchTasksSilently();
        return;
      }

      // Fluxo para épicos: calcula diferença de dias
      console.log("Movendo épico:", task.id);
      const daysDiff = getDaysDifference(
        new Date(tasks.find((t) => t.id === task.id)?.start || task.start),
        task.start
      );

      // Atualiza tarefas dependentes
      const { tasksToUpdate } = await updateDependentTasks(
        task,
        tasks,
        daysDiff,
      );

      // Prepara todas as tarefas para enviar (a principal + dependentes)
      const allTasksToUpdate = [
        { id: task.id, start: task.start, end: task.end },
        ...tasksToUpdate,
      ];

      // Envia ao servidor em paralelo (apenas para épicos)
      const updatePromises = allTasksToUpdate.map((t) =>
        api.put(
          `/atualizarDataEpico/${t.id}`,
          { start: t.start, end: t.end },
          { headers: { "Content-Type": "application/json" } }
        )
      );

      await Promise.all(updatePromises);

      // Atualiza estado local
      await refetchTasksSilently();
    } catch (error) {
      console.error("Erro ao atualizar épico e dependentes:", error);
      // Recarrega as tarefas em caso de erro
      getTasks();
    }
  };

  const updateStoryPlannedStart = useCallback(
    async (task, oldTask) => {
      try {
        const oldDate = new Date(oldTask.start);
        const newDate = new Date(task.start);

        const daysDiff = Math.round(
          (newDate - oldDate) / (1000 * 60 * 60 * 24)
        );

        // Extrai o storyId do formato: story-{epicId}-{storyId}
        const storyId = Number(task.id.split("-")[2]);

        const dependentStories =
          collectDependentStories(storyId);
        
        const storiesToMove = [...new Set([storyId, ...dependentStories])];

        const updatedStories = structuredClone(epicStories);

        await Promise.all(
          storiesToMove.map(async (id) => {
            let story = null;

            Object.values(updatedStories).forEach((stories) => {
              const found = stories.find((s) => s.id === id);

              if (found) {
                story = found;
              }
            });

            if (!story) return;

            let baseDate = story.plannedStartDate
              ? new Date(story.plannedStartDate)
              : null;

            if (!baseDate) {
              const epicIdForStory = storyIdToEpicId[String(id)];
              const renderedTask = visibleTasks.find(
                (t) => t.id === `story-${epicIdForStory}-${id}`
              );
              baseDate = renderedTask ? new Date(renderedTask.start) : null;
            }

            if (!baseDate) return;

            const startDate = new Date(baseDate);
            startDate.setDate(startDate.getDate() + daysDiff);

            let endDate;
            const currentPlannedEnd = parseLocalDate(story.plannedEndDate);

            if (currentPlannedEnd) {
              endDate = addDays(currentPlannedEnd, daysDiff);
            } else {
              const estimatedHours = Number(story.estimated_hours) || 0;
              const durationDays = Math.max(1, Math.ceil(estimatedHours / HOURS_PER_DAY));
              endDate = addDays(startDate, durationDays);
            }

            const formattedStart = startDate.toISOString().split("T")[0];
            const formattedEnd = endDate.toISOString().split("T")[0];

            await api.patch("/atualizarDataHistoria", {
              userStoryId: id,
              start: formattedStart,
              end: formattedEnd,
            });

            story.plannedStartDate = formattedStart;
          }),
        );

        setEpicStories(updatedStories);
      } catch (error) {
        console.error("Erro ao atualizar datas da história:", error);
      }
    },
    [
      epicStories,
      collectDependentStories,
      storyIdToEpicId,
      visibleTasks,
    ]
  );

  const handleProgressChange = (task) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
  };

  const handleDblClick = (task) => {
    setOpenModal(true);
    setTaskSelected({ id: task.id });
  };

  const handleExpanderClick = (task) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
  };
  const loadEpicStories = async (epicId, force = false) => {
    if (!force && (epicStories[epicId] || storiesLoading[epicId])) return;

    try {
      setStoriesLoading((prev) => ({ ...prev, [epicId]: true }));
      const storiesWithProgress = await fetchStoriesWithAttributes(epicId, dataInicio);

      if (!plannedStartAttrIdRef.current) {
        const found = storiesWithProgress.find((s) => s.plannedStartAttrId);
        if (found) plannedStartAttrIdRef.current = found.plannedStartAttrId;
      }

      setEpicStories((prev) => ({ ...prev, [epicId]: storiesWithProgress }));
    } catch (error) {
      console.error(`Erro ao buscar histórias do épico ${epicId}:`, error);
      setEpicStories((prev) => ({ ...prev, [epicId]: [] }));
    } finally {
      setStoriesLoading((prev) => ({ ...prev, [epicId]: false }));
    }
  };

  const toggleEpicAccordion = async (epicId) => {
    const isExpanded = !!expandedEpics[epicId];

    setExpandedEpics((prev) => ({ ...prev, [epicId]: !isExpanded }));

    if (!isExpanded && !epicStories[epicId]) {
      await loadEpicStories(epicId);
    }
  };

  const handleOpenCreateUserStoryModal = (epicId) => {
    setSelectedEpicForStory(epicId);
    setOpenCreateUserStoryModal(true);
  };

  const handleOpenEditUserStoryAttributesModal = (story, epicId) => {
    setSelectedStoryForAttributes(story);
    setSelectedEpicForEditedStory(epicId);
    setOpenEditUserStoryAttributesModal(true);
  };

  const handleOpenManageDependenciesModal = (rawId, name, entityType) => {
    const composedId = `${entityType}-${rawId}`
    setSelectedEntityForDependencies({ id: composedId, name });
    setOpenManageDependenciesModal(true);
  };

  const handleUserStoryCreated = async (newStory) => {
    if (selectedEpicForStory) {
      setExpandedEpics((prev) => ({
        ...prev,
        [selectedEpicForStory]: true,
      }));
    }
    await refetchTasksSilently();
  };
  // ── Impressão ──────────────────────────────────────────────────────────────

  const handlePrint = () => {
    window.print();
  };

  // ── Seletor de view ────────────────────────────────────────────────────────

  const viewOptions = [
    { label: "Dia", value: ViewMode.Day },
    { label: "Semana", value: ViewMode.Week },
    { label: "Mês", value: ViewMode.Month },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ width: "100%" }}>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {/* Seletor de visualização */}
        <div
          style={{
            display: "flex",
            gap: 0,
            border: "1px solid #D1D5DB",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {viewOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setView(opt.value)}
              style={{
                padding: "6px 16px",
                fontSize: 13,
                fontWeight: 500,
                background: view === opt.value ? "#1D4ED8" : "#FFFFFF",
                color: view === opt.value ? "#FFFFFF" : "#374151",
                border: "none",
                cursor: "pointer",
                transition: "background 0.15s, color 0.15s",
                borderRight: "1px solid #D1D5DB",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Legenda */}
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 24, height: 10, borderRadius: 3, background: "#7DD3FC" }} />
            <span style={{ fontSize: 11, color: "#6B7280" }}>Planejamento</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 24, height: 10, borderRadius: 3, background: "#F97316" }} />
            <span style={{ fontSize: 11, color: "#6B7280" }}>Consumo</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 24, height: 10, borderRadius: 3, background: "#1D43D8" }} />
            <span style={{ fontSize: 11, color: "#6B7280" }}>Progresso</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 24, height: 10, borderRadius: 3, background: "#16A43A" }} />
            <span style={{ fontSize: 11, color: "#6B7280" }}>Execução (no prazo)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 24, height: 10, borderRadius: 3, background: "#fa0c0c" }} />
            <span style={{ fontSize: 11, color: "#6B7280" }}>Execução (atrasada)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: "2px solid #6366F1",
                background: "transparent",
              }}
            />
            <span style={{ fontSize: 11, color: "#6B7280" }}>Dependência</span>
          </div>
        </div>

        {/* Botão imprimir */}
        <button
          onClick={handlePrint}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 16px",
            background: "#1D4ED8",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#1E40AF")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#1D4ED8")}
        >
          Imprimir Gantt
        </button>
      </div>

      {/* Gráfico */}
      <div
        ref={ganttRef}
        className="gantt-container-wrapper"
        style={{
          // overflowX: "scroll",
          // overflowY: "auto",
          // maxHeight: "calc(100vh - 220px)",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "3rem", color: "#1D4ED8" }}
            />
          </div>
        ) : visibleTasks.length > 0 ? (
          <Gantt
            tasks={ganttTasks}
            viewMode={view}
            ganttHeight={ganttHeight}
            onDateChange={handleTaskChange}
            onProgressChange={handleProgressChange}
            onDoubleClick={handleDblClick}
            onExpanderClick={handleExpanderClick}
            locale="pt-BR"
            // ganttHeight={420}
            listCellWidth={`${TOTAL_LIST_WIDTH}px`}
            columnWidth={view === ViewMode.Day ? 40 : view === ViewMode.Month ? 150 : 65}
            rowHeight={44}
            barCornerRadius={6}
            TaskListHeader={TaskListHeader}
            TaskListTable={(props) => (
              <TaskListTable
                {...props}
                expandedEpics={expandedEpics}
                toggleEpicAccordion={toggleEpicAccordion}
                epicStories={epicStories}
                storiesLoading={storiesLoading}
                onOpenCreateUserStoryModal={handleOpenCreateUserStoryModal}
                onOpenEditUserStoryAttributesModal={handleOpenEditUserStoryAttributesModal}
                onOpenManageDependenciesModal={handleOpenManageDependenciesModal}
                getAssigneeName={getAssigneeName}
              />
            )}
            // Cores globais (sobrescritas por task.styles individualmente)
            barBackgroundColor="#1D4ED8"
            barProgressColor="#F59E0B"
            barBackgroundSelectedColor="#1E40AF"
            barProgressSelectedColor="#D97706"
            arrowColor="#6366F1"
            arrowIndent={20}
            todayColor="rgba(99,102,241,0.08)"
            TooltipContent={({ task }) => (
              <div
                style={{
                  background: "#1F2937",
                  color: "#F9FAFB",
                  padding: "10px 14px",
                  borderRadius: 8,
                  fontSize: 13,
                  minWidth: 180,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 6, color: "#93C5FD" }}>
                  {task.subject || task.name}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <span>
                    Início {task.start.toLocaleDateString("pt-BR")} até {task.end.toLocaleDateString("pt-BR")}
                  </span>
                  {task.horasPrevistas > 0 && (
                    <span>Previsto: {task.horasPrevistas}h</span>
                  )}
                  {task.horasReais > 0 && (
                    <span style={{ color: "#FCD34D" }}>
                      Real: {task.horasReais}h
                    </span>
                  )}
                  {task.cnsum !== null && (
                    <span style={{ color: cnsumColor(task.cnsum) }}>
                      CNSUM: {task.cnsum}%
                    </span>
                  )}
                  {task.progress > 0 && (
                    <span style={{ color: "#6EE7B7" }}>
                      Concluído: {task.progress}%
                    </span>
                  )}
                  {task.status && (
                    <span style={{ color: "#C4B5FD" }}>Status: {task.status}</span>
                  )}
                </div>
              </div>
            )}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: 200,
              color: "#9CA3AF",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 14 }}>
              Sem dados para exibir. Selecione um projeto e aplique os filtros.
            </span>
          </div>
        )}
      </div>

      <ModalEditNameEpic
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        task={taskSelected}
        getTasks={getTasks}
      />

      <ModalCreateUserStory
        isOpen={openCreateUserStoryModal}
        onClose={() => setOpenCreateUserStoryModal(false)}
        epicId={selectedEpicForStory}
        projectId={idProjeto}
        users={users}
        onSuccess={handleUserStoryCreated}
      />

      <ModalManageDependencies
        isOpen={openManageDependenciesModal}
        onClose={() => {
          setOpenManageDependenciesModal(false);
          setSelectedEntityForDependencies(null);
        }}
        epicId={selectedEntityForDependencies?.id}
        epicName={selectedEntityForDependencies?.name}
        allTasks={dependencyCandidates}
        currentDependencies={[]}
        onSuccess={getTasks}
      />

      <ModalEditUserStoryAttributes
        isOpen={openEditUserStoryAttributesModal}
        onClose={() => {
          setOpenEditUserStoryAttributesModal(false);
          setSelectedStoryForAttributes(null);
        }}
        story={selectedStoryForAttributes}
        onSuccess={ async () => {
          if (selectedEpicForEditedStory) {
            setExpandedEpics((prev) => ({ ...prev, [selectedEpicForEditedStory]: true }));
          }
          await refetchTasksSilently();
        }}
      />
    </div>
  );
}

export default GanttChartEnhanced;
